import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../../api/appsync-client.js'
import { useSubscription } from '../../api/useSubscription.js'
import { parseTimeHorizon } from '../timeHorizon.js'

function parseGoal(goal) {
  if (!goal) return goal
  const parsed = { ...goal, timeHorizon: parseTimeHorizon(goal.timeHorizon) }
  if (parsed.subgoals) {
    parsed.subgoals = parsed.subgoals.map((sg) => ({
      ...sg,
      timeHorizon: parseTimeHorizon(sg.timeHorizon),
    }))
  }
  return parsed
}

const GOAL_FIELDS = `
  goalId ownerUserId managerId createdBy
  title description goalType timeHorizon
  status statusLabel
  createdAt updatedAt achievedAt droppedAt droppedReason
  rollup { total notStarted onTrack atRisk blocked done }
  subgoals { subgoalId goalId title primaryFacet facets status notes timeHorizon createdAt updatedAt }
`

const LIST_GOALS = `
  query ListGoals($ownerUserId: ID, $status: String) {
    listGoals(ownerUserId: $ownerUserId, status: $status) {
      ${GOAL_FIELDS}
    }
  }
`

const CREATE_GOAL = `
  mutation CreateGoal($input: CreateGoalInput!) {
    createGoal(input: $input) {
      ${GOAL_FIELDS}
    }
  }
`

const UPDATE_GOAL = `
  mutation UpdateGoal($goalId: ID!, $ownerUserId: ID, $input: UpdateGoalInput!) {
    updateGoal(goalId: $goalId, ownerUserId: $ownerUserId, input: $input) {
      ${GOAL_FIELDS}
    }
  }
`

const DELETE_GOAL = `
  mutation DeleteGoal($goalId: ID!, $ownerUserId: ID) {
    deleteGoal(goalId: $goalId, ownerUserId: $ownerUserId)
  }
`

const ACTIVATE_GOAL = `
  mutation ActivateGoal($goalId: ID!, $ownerUserId: ID) {
    activateGoal(goalId: $goalId, ownerUserId: $ownerUserId) {
      ${GOAL_FIELDS}
    }
  }
`

const ACHIEVE_GOAL = `
  mutation AchieveGoal($goalId: ID!, $ownerUserId: ID) {
    achieveGoal(goalId: $goalId, ownerUserId: $ownerUserId) {
      ${GOAL_FIELDS}
    }
  }
`

const DROP_GOAL = `
  mutation DropGoal($goalId: ID!, $ownerUserId: ID, $reason: String!) {
    dropGoal(goalId: $goalId, ownerUserId: $ownerUserId, reason: $reason) {
      ${GOAL_FIELDS}
    }
  }
`

const REOPEN_GOAL = `
  mutation ReopenGoal($goalId: ID!, $ownerUserId: ID) {
    reopenGoal(goalId: $goalId, ownerUserId: $ownerUserId) {
      ${GOAL_FIELDS}
    }
  }
`

const SET_STATUS_LABEL = `
  mutation SetGoalStatusLabel($goalId: ID!, $ownerUserId: ID, $statusLabel: String!) {
    setGoalStatusLabel(goalId: $goalId, ownerUserId: $ownerUserId, statusLabel: $statusLabel) {
      ${GOAL_FIELDS}
    }
  }
`

const ON_GOAL_LIFECYCLE_CHANGED = `
  subscription OnGoalLifecycleChanged($ownerUserId: ID!) {
    onGoalLifecycleChanged(ownerUserId: $ownerUserId) {
      ${GOAL_FIELDS}
    }
  }
`

export function useGoals({ ownerUserId = null, enabled = true } = {}) {
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(LIST_GOALS, { ownerUserId })
      setGoals((data.listGoals || []).map(parseGoal))
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [ownerUserId, enabled])

  useEffect(() => { load() }, [load])

  // Refetch when tab regains focus
  useEffect(() => {
    if (!enabled) return
    const handler = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [enabled, load])

  useSubscription(
    ON_GOAL_LIFECYCLE_CHANGED,
    { ownerUserId },
    (data) => {
      const updated = parseGoal(data.onGoalLifecycleChanged)
      if (!updated) return
      setGoals((prev) => {
        const exists = prev.some((g) => g.goalId === updated.goalId)
        if (exists) return prev.map((g) => (g.goalId === updated.goalId ? updated : g))
        return [...prev, updated]
      })
    },
    { enabled: enabled && !!ownerUserId },
  )

  const createGoal = useCallback(async (body) => {
    const input = { ...body }
    if (ownerUserId) input.ownerUserId = ownerUserId
    const data = await appsyncQuery(CREATE_GOAL, { input })
    const goal = parseGoal(data.createGoal)
    setGoals((prev) => [goal, ...prev])
    return goal
  }, [ownerUserId])

  const updateGoal = useCallback(async (goalId, body) => {
    const data = await appsyncQuery(UPDATE_GOAL, { goalId, ownerUserId, input: body })
    const goal = parseGoal(data.updateGoal)
    setGoals((prev) => prev.map((g) => (g.goalId === goalId ? goal : g)))
    return goal
  }, [ownerUserId])

  const deleteGoal = useCallback(async (goalId) => {
    await appsyncQuery(DELETE_GOAL, { goalId, ownerUserId })
    setGoals((prev) => prev.filter((g) => g.goalId !== goalId))
  }, [ownerUserId])

  const activateGoal = useCallback(async (goalId) => {
    const data = await appsyncQuery(ACTIVATE_GOAL, { goalId, ownerUserId })
    const goal = parseGoal(data.activateGoal)
    setGoals((prev) => prev.map((g) => (g.goalId === goalId ? goal : g)))
    return goal
  }, [ownerUserId])

  const achieveGoal = useCallback(async (goalId) => {
    const data = await appsyncQuery(ACHIEVE_GOAL, { goalId, ownerUserId })
    const goal = parseGoal(data.achieveGoal)
    setGoals((prev) => prev.map((g) => (g.goalId === goalId ? goal : g)))
    return goal
  }, [ownerUserId])

  const dropGoal = useCallback(async (goalId, reason) => {
    const data = await appsyncQuery(DROP_GOAL, { goalId, ownerUserId, reason })
    const goal = parseGoal(data.dropGoal)
    setGoals((prev) => prev.map((g) => (g.goalId === goalId ? goal : g)))
    return goal
  }, [ownerUserId])

  const reopenGoal = useCallback(async (goalId) => {
    const data = await appsyncQuery(REOPEN_GOAL, { goalId, ownerUserId })
    const goal = parseGoal(data.reopenGoal)
    setGoals((prev) => prev.map((g) => (g.goalId === goalId ? goal : g)))
    return goal
  }, [ownerUserId])

  const setStatusLabel = useCallback(async (goalId, statusLabel) => {
    const data = await appsyncQuery(SET_STATUS_LABEL, { goalId, ownerUserId, statusLabel })
    const goal = parseGoal(data.setGoalStatusLabel)
    setGoals((prev) => prev.map((g) => (g.goalId === goalId ? goal : g)))
    return goal
  }, [ownerUserId])

  return {
    goals,
    loading,
    error,
    reload: load,
    createGoal,
    updateGoal,
    deleteGoal,
    activateGoal,
    achieveGoal,
    dropGoal,
    reopenGoal,
    setStatusLabel,
  }
}
